import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import Select, { components, GroupBase, MenuListProps } from "react-select";

import {
  SecondaryButton,
  RadioButton,
  DangerSecondaryButton,
} from "@graffer-inc/console-design-kit";

import { ErrorMessage } from "../../atoms/error-message";
import { Group, GroupRole } from "../../../application/domain/group/group";
import { UserConst } from "../../../application/domain/user/user";
import { displayGroupRole } from "../../../application/domain/rbac/rbac";
import { useGroupSelector } from "./use-group-selector";

export type GroupSelectorProps = {
  editingGroupRoles: { [key: string]: GroupRole };
  setGroupRoles: (groupRoles: { [key: string]: GroupRole }) => void;
  validationError: string | undefined;
  orgId: string;
  isServiceProvider: boolean;
};

export const GroupSelector: React.FC<GroupSelectorProps> = ({
  editingGroupRoles,
  setGroupRoles,
  validationError,
  orgId,
  isServiceProvider,
}) => {
  const {
    isGroupsRoles,
    addGroup,
    page,
    setPage,
    isDisplayBackButton,
    isDisplayNextButton,
    setName,
    candidateGroups,
  } = useGroupSelector({
    editingGroupRoles,
    setGroupRoles,
    orgId,
    isServiceProvider,
  });

  return (
    <>
      <table className="table is-fullwidth mb-0">
        <thead>
          <tr>
            <th style={{ minWidth: "360px" }}>グループ名</th>
            <th>ロール</th>
            <th aria-label="所属を解除する"></th>
          </tr>
        </thead>
        <tbody>
          {isGroupsRoles && (
            <>
              {Object.keys(editingGroupRoles).map((groupId) => (
                <React.Fragment key={groupId}>
                  <GroupSelectorRow
                    editingGroupRoles={editingGroupRoles}
                    setGroupRoles={setGroupRoles}
                    setSearchName={setName}
                    groupRole={{
                      groupId: groupId,
                      role: editingGroupRoles[groupId],
                    }}
                    groupCandidates={Array.from(candidateGroups.values())}
                    page={page}
                    setPage={setPage}
                    isDisplayBackButton={isDisplayBackButton}
                    isDisplayNextButton={isDisplayNextButton}
                  />
                </React.Fragment>
              ))}
            </>
          )}
        </tbody>
      </table>
      <AddButton
        inputUserInfoGroupsCount={Object.keys(editingGroupRoles).length}
        addUserGroups={addGroup}
      />
      <ErrorMessage msg={validationError} />
    </>
  );
};

const AddButton: React.FC<{
  inputUserInfoGroupsCount: number;
  addUserGroups: () => void;
}> = ({ inputUserInfoGroupsCount, addUserGroups }) => {
  if (inputUserInfoGroupsCount >= UserConst.MAX_GROUPS_LENGTH) {
    return (
      <p className="help has-text-weight-normal mt-2">
        最大で{UserConst.MAX_GROUPS_LENGTH}個までグループに所属できます
      </p>
    );
  }
  return (
    <>
      <SecondaryButton text={"グループを追加する"} onClick={addUserGroups} />
      <p className="help has-text-weight-normal mt-2">
        最大で{UserConst.MAX_GROUPS_LENGTH}個までグループに所属できます
      </p>
    </>
  );
};

type GroupSelectorRowProps = {
  editingGroupRoles: { [key: string]: GroupRole };
  setGroupRoles: (groupRoles: { [key: string]: GroupRole }) => void;
  setSearchName: (name: string) => void;
  groupRole: { groupId: string; role: string };
  groupCandidates: Group[];
  page: number;
  setPage: (page: number) => void;
  isDisplayBackButton: boolean;
  isDisplayNextButton: boolean;
};

type OptionType = {
  label: string;
  value: string;
  isIgnoreAclSetting: boolean;
};

const GroupSelectorRow: React.FC<GroupSelectorRowProps> = ({
  editingGroupRoles,
  setGroupRoles,
  setSearchName,
  groupRole,
  groupCandidates,
  page,
  setPage,
  isDisplayBackButton,
  isDisplayNextButton,
}) => {
  const [isDisplayGroupWarning, setIsDisplayGroupWarning] =
    useState<boolean>(false);

  const options: OptionType[] = useMemo<OptionType[]>(() => {
    // ユーザーが選択しているグループはセレクトボックスの選択肢に含めたくない
    const selectedGroup = Object.keys(editingGroupRoles).filter(
      (gId) => gId !== groupRole.groupId,
    );
    return groupCandidates
      .filter((g) => !selectedGroup.includes(g.id!))
      .map((g) => ({
        label: g.name,
        value: g.id!,
        isIgnoreAclSetting: g.isIgnoreAclSetting,
      }));
  }, [editingGroupRoles, groupCandidates, groupRole.groupId]);

  const deleteGroup = useCallback(() => {
    const editingGroupRolesCopy = { ...editingGroupRoles };
    delete editingGroupRolesCopy[groupRole.groupId];
    setGroupRoles(editingGroupRolesCopy);
  }, [editingGroupRoles, setGroupRoles, groupRole.groupId]);

  return (
    <tr>
      <td>
        <Select
          placeholder="選択またはキーワードで検索"
          value={options.find((g) => g.value === groupRole.groupId)}
          onInputChange={setSearchName}
          onChange={(selectedOption) => {
            if (selectedOption && selectedOption.value) {
              const groupRoles = {
                ...editingGroupRoles,
                [selectedOption.value]: editingGroupRoles[groupRole.groupId],
              };
              delete groupRoles[groupRole.groupId];

              setGroupRoles(groupRoles);
              setIsDisplayGroupWarning(selectedOption.isIgnoreAclSetting);
            }
          }}
          options={options}
          components={{
            MenuList: MenuListBuilder({
              isDisplayBackButton: isDisplayBackButton,
              isDisplayNextButton: isDisplayNextButton,
              backPage: () => setPage(page - 1),
              nextPage: () => setPage(page + 1),
            }),
          }}
        />
        <p className="help is-danger"></p>
      </td>
      <td>
        <div>
          <RadioButton
            description={""}
            label={""}
            onChange={(e) => {
              if (e.target.value === "GroupManager") {
                setGroupRoles({
                  ...editingGroupRoles,
                  [groupRole.groupId]: "GroupManager",
                });
              } else {
                setGroupRoles({
                  ...editingGroupRoles,
                  [groupRole.groupId]: "Staff",
                });
              }
            }}
            options={[
              { name: displayGroupRole("GroupManager"), value: "GroupManager" },
              { name: displayGroupRole("Staff"), value: "Staff" },
            ]}
            value={groupRole.role}
            direction="row"
          />
        </div>
        {isDisplayGroupWarning && (
          <div className="notification is-warning is-light mb-4">
            <div>
              <span className={`icon has-text-warning`}>
                <i className="fas fa-exclamation-triangle" />
              </span>
              <b>IPアドレス制御が無効化されているグループです</b>
            </div>
            <div className="mt-2 is-size-7">
              <p>
                IPアドレス制御を無効化して問題ないユーザーかどうかを確認してください。
              </p>
            </div>
          </div>
        )}
      </td>
      <td>
        {Object.keys(editingGroupRoles).length > 1 && (
          <DangerSecondaryButton
            onClick={() => deleteGroup()}
            text="所属を解除する"
          />
        )}
      </td>
    </tr>
  );
};

const MenuListBuilder = (arg: {
  isDisplayBackButton: boolean;
  isDisplayNextButton: boolean;
  backPage: () => void;
  nextPage: () => void;
}):
  | React.ComponentType<MenuListProps<OptionType, false, GroupBase<OptionType>>>
  | undefined => {
  const GroupMenuList:
    | React.ComponentType<
        MenuListProps<OptionType, false, GroupBase<OptionType>>
      >
    | undefined = (props) => {
    return (
      <>
        <components.MenuList {...props}>
          {props.children}
          {(arg.isDisplayBackButton || arg.isDisplayNextButton) && (
            <div className="m-4 is-flex is-justify-content-center">
              {arg.isDisplayBackButton && (
                <button
                  className="button is-size-7 mr-3"
                  onClick={arg.backPage}
                >
                  <span className="icon has-text-dark">
                    <i className="fas fa-chevron-left"></i>
                  </span>
                </button>
              )}
              {arg.isDisplayNextButton && (
                <button className="button is-size-7" onClick={arg.nextPage}>
                  <span className="icon has-text-dark">
                    <i className="fas fa-chevron-right"></i>
                  </span>
                </button>
              )}
            </div>
          )}
        </components.MenuList>
      </>
    );
  };

  return GroupMenuList;
};
